/**
 * m3 echo Frontend - Benutzerverwaltung
 *
 * Seite zur Verwaltung von Benutzern (CRUD operations).
 */

import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import {
  userApi,
  tenantApi,
  UserWithTenant,
  UserCreate,
  UserUpdate,
  Tenant,
} from '../../services/admin';
import UserTable from '../../components/admin/UserTable';
import UserDialog from '../../components/admin/UserDialog';
import PasswordResetDialog from '../../components/admin/PasswordResetDialog';

interface UserManagementProps {
  embedded?: boolean;
}

export const UserManagement: React.FC<UserManagementProps> = ({ embedded = false }) => {
  const [users, setUsers] = useState<UserWithTenant[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserWithTenant[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tenantFilter, setTenantFilter] = useState<string>('all');

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedUser, setSelectedUser] = useState<UserWithTenant | null>(null);

  // Löschen confirmation dialog
  const [deleteDialogOpen, setLöschenDialogOpen] = useState(false);
  const [userToLöschen, setUserToLöschen] = useState<UserWithTenant | null>(null);

  // Password reset dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [userToReset, setUserToReset] = useState<UserWithTenant | null>(null);

  // Toast notification
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Filter users based on search query and tenant filter
    let filtered = users;

    // Apply tenant filter
    if (tenantFilter !== 'all') {
      if (tenantFilter === 'none') {
        filtered = filtered.filter((user) => !user.tenant_id);
      } else {
        filtered = filtered.filter((user) => user.tenant_id === parseInt(tenantFilter));
      }
    }

    // Apply search query
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.email.toLowerCase().includes(query) ||
          (user.full_name && user.full_name.toLowerCase().includes(query))
      );
    }

    setFilteredUsers(filtered);
  }, [searchQuery, tenantFilter, users]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [usersResponse, tenantsResponse] = await Promise.all([
        userApi.getAll(),
        tenantApi.getOptions(),
      ]);
      setUsers(usersResponse.data);
      setFilteredUsers(usersResponse.data);
      setTenants(tenantsResponse.data);
    } catch (err: any) {
      console.error('Fehler beim Laden der Daten:', err);
      setError(err.message || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setDialogMode('create');
    setSelectedUser(null);
    setDialogOpen(true);
  };

  const handleEdit = (user: UserWithTenant) => {
    setDialogMode('edit');
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const handleLöschenClick = (user: UserWithTenant) => {
    setUserToLöschen(user);
    setLöschenDialogOpen(true);
  };

  const handleLöschenConfirm = async () => {
    if (!userToLöschen) return;

    try {
      await userApi.delete(userToLöschen.id);
      showToast(`User "${userToLöschen.email}" erfolgreich gelöscht`);
      setLöschenDialogOpen(false);
      setUserToLöschen(null);
      loadData();
    } catch (err: any) {
      console.error('Fehler beim Löschen des Benutzers:', err);
      setError(err.message || 'Fehler beim Löschen des Benutzers');
      setLöschenDialogOpen(false);
    }
  };

  const handleResetPassword = (user: UserWithTenant) => {
    setUserToReset(user);
    setResetDialogOpen(true);
  };

  const handleResetPasswordConfirm = async () => {
    if (!userToReset) return { temporary_password: '' };
    const response = await userApi.resetPassword(userToReset.id);
    return response.data;
  };

  const handleToggleStatus = async (user: UserWithTenant) => {
    try {
      await userApi.toggleStatus(user.id);
      showToast(
        user.is_active
          ? `Benutzer "${user.email}" wurde deaktiviert`
          : `Benutzer "${user.email}" wurde aktiviert`
      );
      loadData();
    } catch (err: any) {
      console.error('Fehler beim Ändern des Status:', err);
      setError(err.response?.data?.detail || err.message || 'Fehler beim Ändern des Status');
    }
  };

  const handleSave = async (data: UserCreate | UserUpdate) => {
    if (dialogMode === 'create') {
      const response = await userApi.create(data as UserCreate);
      console.log('Create user response:', response.data);
      console.log('Temporary password:', response.data.temporary_password);
      // Only show toast if no temp password (dialog will show password instead)
      if (!response.data.temporary_password) {
        showToast('User erfolgreich erstellt');
        loadData();
      }
      // Return the response which may contain temporary_password
      // loadData will be called when dialog closes
      return response.data;
    } else if (selectedUser) {
      await userApi.update(selectedUser.id, data as UserUpdate);
      showToast('User erfolgreich aktualisiert');
      loadData();
      return {};
    }
    return {};
  };

  const showToast = (message: string) => {
    setToastMessage(message);
    setToastOpen(true);
  };

  const Wrapper = embedded ? React.Fragment : Container;
  const wrapperProps = embedded ? {} : { maxWidth: 'xl' as const, sx: { mt: 4, mb: 4 } };

  if (loading) {
    return (
      <Wrapper {...wrapperProps}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Wrapper>
    );
  }

  return (
    <Wrapper {...wrapperProps}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {!embedded && (
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Benutzerverwaltung
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Alle Benutzer im System verwalten
            </Typography>
          </Box>
        )}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={embedded ? { ml: 'auto' } : {}}
        >
          Benutzer Erstellen
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <TextField
          fullWidth
          placeholder="Benutzer nach E-Mail oder Name durchsuchen..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />
        <TextField
          select
          label="Nach Mandant filtern"
          value={tenantFilter}
          onChange={(e) => setTenantFilter(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="all">Alle Mandanten</MenuItem>
          <MenuItem value="none">Kein Mandant (Nur Admin)</MenuItem>
          {tenants.map((tenant) => (
            <MenuItem key={tenant.id} value={tenant.id.toString()}>
              {tenant.display_name}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* User Table */}
      <UserTable
        users={filteredUsers}
        onEdit={handleEdit}
        onDelete={handleLöschenClick}
        onResetPassword={handleResetPassword}
        onToggleStatus={handleToggleStatus}
      />

      {/* Create/Edit Dialog */}
      <UserDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          loadData(); // Refresh data when dialog closes
        }}
        onSave={handleSave}
        user={selectedUser}
        tenants={tenants}
        mode={dialogMode}
      />

      {/* Password Reset Dialog */}
      <PasswordResetDialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        onReset={handleResetPasswordConfirm}
        user={userToReset}
      />

      {/* Löschen Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setLöschenDialogOpen(false)}>
        <DialogTitle>Benutzer Löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie den Benutzer löschen möchten "{userToLöschen?.email}"? Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLöschenDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleLöschenConfirm} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        message={toastMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Wrapper>
  );
};

export default UserManagement;
